// Configuração para upload via Hostinger (sem limites como Supabase)
const UPLOAD_BASE_URL = '/upmidia/uploads'; // Caminho relativo ao domínio do subdomínio
const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB - limite do navegador (pode aumentar se desejar)

export const storageService = {
  /**
   * Upload a file to Hostinger (via FormData)
   * @param {File} file - The file to upload
   * @param {string} folder - Optional folder path (e.g., 'images', 'videos')
   * @returns {Promise<{url: string, path: string}>} - Public URL and file path
   */
  async uploadFile(file, folder = '') {
    try {
      // Validate file size
      if (file?.size > MAX_FILE_SIZE) {
        const sizeMB = (MAX_FILE_SIZE / (1024 * 1024))?.toFixed(0);
        throw new Error(`Arquivo muito grande. O limite é ${sizeMB}MB por arquivo.`);
      }

      // Generate unique filename with timestamp
      const timestamp = Date.now();
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${timestamp}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
      const filePath = folder ? `${folder}/${fileName}` : fileName;

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);
      formData.append('fileName', fileName);

      // Upload file to Hostinger server
      const response = await fetch(`${UPLOAD_BASE_URL}/api/upload.php`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.message || `Upload failed with status ${response.status}`);
      }

      const data = await response.json();
      
      if (!data?.success) {
        throw new Error(data?.message || 'Upload failed');
      }

      // Return public URL and path
      return {
        url: data?.url, // Full URL to the file
        path: filePath
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  },


  /**
   * Upload multiple files
   * @param {File[]} files - Array of files to upload
   * @param {string} folder - Optional folder path
   * @param {Function} onProgress - Progress callback (fileIndex, progress)
   * @returns {Promise<Array>} - Array of upload results
   */
  async uploadMultipleFiles(files, folder = '', onProgress = null) {
    const results = [];

    for (let i = 0; i < files?.length; i++) {
      try {
        if (onProgress) {
          onProgress(i, 0);
        }

        const result = await this.uploadFile(files?.[i], folder);
        results?.push({ success: true, ...result, file: files?.[i] });

        if (onProgress) {
          onProgress(i, 100);
        }
      } catch (error) {
        results?.push({ success: false, error: error?.message, file: files?.[i] });
        if (onProgress) {
          onProgress(i, -1); // -1 indicates error
        }
      }
    }

    return results;
  },

  /**
   * Delete a file from Hostinger
   * @param {string} fileUrl - URL or path to the file
   * @returns {Promise<boolean>}
   */
  async deleteFile(fileUrl) {
    try {
      // Send delete request to server
      const response = await fetch(`${UPLOAD_BASE_URL}/api/delete.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fileUrl })
      });

      if (!response.ok) {
        throw new Error(`Delete failed with status ${response.status}`);
      }

      const data = await response.json();
      return data?.success || false;
    } catch (error) {
      console.error('Error deleting file:', error);
      // Don't throw - deletion failures shouldn't break the app
      return false;
    }
  },

  /**
   * Get public URL for a file
   * For Hostinger, we construct the URL directly
   * @param {string} filePath - Path to the file
   * @returns {string} - Public URL
   */
  getPublicUrl(filePath) {
    return `${UPLOAD_BASE_URL}/${filePath}`;
  },

  /**
   * List files in a folder (requires backend implementation)
   * @param {string} folder - Folder path
   * @returns {Promise<Array>} - Array of files
   */
  async listFiles(folder = '') {
    try {
      const response = await fetch(`${UPLOAD_BASE_URL}/api/list.php?folder=${folder}`);
      
      if (!response.ok) {
        throw new Error(`List failed with status ${response.status}`);
      }

      const data = await response.json();
      return data?.files || [];
    } catch (error) {
      console.error('Error listing files:', error);
      return [];
    }
  }
};